import { euro, euroPrecise, normalizeQty } from "../lib/constants";

/**
 * PrintableOrder — documento d'ordine fornitore stile editoriale, ottimizzato per stampa A4.
 * `result.groups: [{ supplier_id, supplier_name, supplier_email?, supplier_phone?, lines, total }]`
 * `user` contiene i dati del ristorante per l'intestazione.
 */
export function PrintableOrder({ result, user }) {
    const today = new Date().toLocaleDateString("it-IT", {
        day: "2-digit", month: "long", year: "numeric",
    });
    const orderNumber = `ORD-${new Date().getFullYear()}${String(new Date().getMonth() + 1).padStart(2, "0")}${String(new Date().getDate()).padStart(2, "0")}-${String(new Date().getHours()).padStart(2, "0")}${String(new Date().getMinutes()).padStart(2, "0")}`;
    const restName = user?.restaurant_name || user?.name || "RENOVA360";
    const hasContact = user?.restaurant_address || user?.vat_number || user?.phone || user?.contact_email;

    return (
        <div className="bg-white text-black" style={{ fontFamily: "Manrope, sans-serif" }}>
            {result.groups.map((g, gi) => {
                const isGeneric = g.generic === true || g.supplier_id === null;
                return (
                <section
                    key={g.supplier_id || `generic-${gi}`}
                    className="print-supplier-block p-10"
                    style={{
                        pageBreakAfter: gi < result.groups.length - 1 ? "always" : "auto",
                        breakAfter: gi < result.groups.length - 1 ? "page" : "auto",
                    }}
                >
                    <header className="print-keep-together">
                    <div className="flex items-start justify-between border-b-2 border-black pb-4 mb-6">
                        <div className="min-w-0">
                            <div
                                className="text-2xl font-bold tracking-tight"
                                style={{ fontFamily: "Playfair Display, serif" }}
                            >
                                {restName}
                            </div>
                            {hasContact && (
                                <div className="mt-1.5 text-[11px] leading-relaxed text-black/75">
                                    {user?.restaurant_address && <div>{user.restaurant_address}</div>}
                                    {user?.restaurant_city && <div>{user.restaurant_city}</div>}
                                    {user?.vat_number && <div>P.IVA / C.F.: {user.vat_number}</div>}
                                    {user?.phone && <div>Tel: {user.phone}</div>}
                                    {user?.contact_email && <div>{user.contact_email}</div>}
                                </div>
                            )}
                        </div>
                        <div className="text-right text-[11px] text-black/70 shrink-0 ml-6">
                            <div className="text-[9px] uppercase tracking-[0.2em] font-bold text-black/60">
                                Documento
                            </div>
                            <div className="mt-0.5 text-sm font-bold text-black">{orderNumber}-{gi + 1}</div>
                            <div className="mt-1">{today}</div>
                        </div>
                    </div>

                    <div className="mb-5">
                        <div
                            className="text-[10px] uppercase tracking-[0.22em] font-bold"
                            style={{ color: isGeneric ? "#a16207" : "rgba(0,0,0,0.6)" }}
                        >
                            {isGeneric ? "Lista senza fornitore" : "Ordine a fornitore"}
                        </div>
                        <div
                            className="text-3xl font-bold mt-1"
                            style={{ fontFamily: "Playfair Display, serif" }}
                        >
                            {isGeneric ? "Articoli da assegnare a un fornitore" : g.supplier_name}
                        </div>
                        {isGeneric ? (
                            <div className="mt-1 text-[11px] text-black/70 italic">
                                Articoli non presenti in alcun listino. Da compilare manualmente o da
                                assegnare a un nuovo fornitore.
                            </div>
                        ) : (
                            (g.supplier_email || g.supplier_phone) && (
                                <div className="mt-1 text-[11px] text-black/70">
                                    {g.supplier_email && <span>{g.supplier_email}</span>}
                                    {g.supplier_email && g.supplier_phone && <span> · </span>}
                                    {g.supplier_phone && <span>{g.supplier_phone}</span>}
                                </div>
                            )
                        )}
                    </div>
                    </header>

                    <table className="w-full text-sm border-collapse">
                        <thead>
                            <tr className="border-y-2 border-black bg-black/5">
                                <th className="text-left py-2 px-2 text-[10px] uppercase tracking-wider">#</th>
                                <th className="text-left py-2 px-2 text-[10px] uppercase tracking-wider">Articolo</th>
                                <th className="text-right py-2 px-2 text-[10px] uppercase tracking-wider">Qtà</th>
                                <th className="text-right py-2 px-2 text-[10px] uppercase tracking-wider">UM</th>
                                <th className="text-right py-2 px-2 text-[10px] uppercase tracking-wider">€ unit.</th>
                                <th className="text-right py-2 px-2 text-[10px] uppercase tracking-wider">Totale</th>
                            </tr>
                        </thead>
                        <tbody>
                            {g.lines.map((l, i) => {
                                const blank = isGeneric && (!l.unit_price || l.unit_price === 0);
                                const norm = normalizeQty(l.quantity, l.unit);
                                return (
                                    <tr key={i} className="border-b border-black/15">
                                        <td className="py-2 px-2 text-black/60">{i + 1}</td>
                                        <td className="py-2 px-2 font-medium">{l.ingredient_name}</td>
                                        <td className="text-right py-2 px-2">{parseFloat(norm.qty.toFixed(3))}</td>
                                        <td className="text-right py-2 px-2 uppercase">{norm.unit}</td>
                                        <td className="text-right py-2 px-2">
                                            {blank ? <span className="text-black/30">__________</span> : euroPrecise(l.unit_price)}
                                        </td>
                                        <td className="text-right py-2 px-2 font-semibold">
                                            {blank ? <span className="text-black/30">__________</span> : euro(l.line_total)}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                        <tfoot>
                            <tr className="border-t-2 border-black">
                                <td colSpan="5" className="text-right pt-3 px-2 font-bold text-base">
                                    Totale ordine
                                </td>
                                <td className="text-right pt-3 px-2 font-bold text-lg">
                                    {isGeneric && g.total === 0 ? <span className="text-black/30">__________</span> : euro(g.total)}
                                </td>
                            </tr>
                        </tfoot>
                    </table>

                    <div className="print-keep-together">
                    <div className="mt-10 grid grid-cols-2 gap-8 text-[11px] text-black/70">
                        <div>
                            <div className="text-[9px] uppercase tracking-wider font-bold text-black/60 mb-1">
                                {isGeneric ? "Fornitore" : "Note"}
                            </div>
                            <div className="border-b border-black/40 h-12" />
                            <div className="border-b border-black/40 h-6 mt-1" />
                        </div>
                        <div>
                            <div className="text-[9px] uppercase tracking-wider font-bold text-black/60 mb-1">
                                Firma per accettazione
                            </div>
                            <div className="border-b border-black/40 h-12" />
                            <div className="mt-1 italic">Data ____________________</div>
                        </div>
                    </div>

                    <div className="mt-10 pt-3 border-t border-black/30 text-[9px] uppercase tracking-[0.2em] text-black/50 text-center">
                        Documento generato da RENOVA360 · Food Cost Manager
                    </div>
                    </div>
                </section>
                );
            })}

            {result.unassigned && result.unassigned.length > 0 && (
                <section
                    className="print-supplier-block p-10"
                    style={{
                        pageBreakBefore: "always",
                        breakBefore: "page",
                        pageBreakAfter: result.groups.length > 1 ? "always" : "auto",
                        breakAfter: result.groups.length > 1 ? "page" : "auto",
                    }}
                >
                    <header className="print-keep-together">
                        <div className="flex items-start justify-between border-b-2 border-black pb-4 mb-6">
                            <div className="min-w-0">
                                <div
                                    className="text-2xl font-bold tracking-tight"
                                    style={{ fontFamily: "Playfair Display, serif" }}
                                >
                                    {restName}
                                </div>
                                {hasContact && (
                                    <div className="mt-1.5 text-[11px] leading-relaxed text-black/75">
                                        {user?.restaurant_address && <div>{user.restaurant_address}</div>}
                                        {user?.restaurant_city && <div>{user.restaurant_city}</div>}
                                        {user?.vat_number && <div>P.IVA / C.F.: {user.vat_number}</div>}
                                        {user?.phone && <div>Tel: {user.phone}</div>}
                                        {user?.contact_email && <div>{user.contact_email}</div>}
                                    </div>
                                )}
                            </div>
                            <div className="text-right text-[11px] text-black/70 shrink-0 ml-6">
                                <div className="text-[9px] uppercase tracking-[0.2em] font-bold text-black/60">
                                    Documento
                                </div>
                                <div className="mt-0.5 text-sm font-bold text-black">{orderNumber}-X</div>
                                <div className="mt-1">{today}</div>
                            </div>
                        </div>

                        <div className="mb-5">
                            <div
                                className="text-[10px] uppercase tracking-[0.22em] font-bold"
                                style={{ color: "#a16207" }}
                            >
                                Articoli senza fornitore
                            </div>
                            <div
                                className="text-3xl font-bold mt-1"
                                style={{ fontFamily: "Playfair Display, serif" }}
                            >
                                Lista acquisti da associare a fornitore
                            </div>
                            <div className="mt-1 text-[11px] text-black/70 italic">
                                Articoli non presenti in alcun listino. Da compilare manualmente o
                                da assegnare a un fornitore.
                            </div>
                        </div>
                    </header>

                    <table className="w-full text-sm border-collapse">
                        <thead>
                            <tr className="border-y-2 border-black bg-black/5">
                                <th className="text-left py-2 px-2 text-[10px] uppercase tracking-wider">#</th>
                                <th className="text-left py-2 px-2 text-[10px] uppercase tracking-wider">Articolo</th>
                                <th className="text-right py-2 px-2 text-[10px] uppercase tracking-wider">Qtà</th>
                                <th className="text-right py-2 px-2 text-[10px] uppercase tracking-wider">UM</th>
                                <th className="text-left py-2 px-2 text-[10px] uppercase tracking-wider">Fornitore</th>
                                <th className="text-right py-2 px-2 text-[10px] uppercase tracking-wider">€ unit.</th>
                                <th className="text-right py-2 px-2 text-[10px] uppercase tracking-wider">Totale</th>
                            </tr>
                        </thead>
                        <tbody>
                            {result.unassigned.map((l, i) => {
                                const norm = normalizeQty(l.quantity, l.unit);
                                return (
                                <tr key={i} className="border-b border-black/15">
                                    <td className="py-2 px-2 text-black/60">{i + 1}</td>
                                    <td className="py-2 px-2 font-medium">{l.ingredient_name}</td>
                                    <td className="text-right py-2 px-2">{parseFloat(norm.qty.toFixed(3))}</td>
                                    <td className="text-right py-2 px-2 uppercase">{norm.unit}</td>
                                    <td className="py-2 px-2 text-black/30">__________________</td>
                                    <td className="text-right py-2 px-2 text-black/30">__________</td>
                                    <td className="text-right py-2 px-2 text-black/30">__________</td>
                                </tr>
                                );
                            })}
                        </tbody>
                    </table>

                    <div className="print-keep-together">
                        <div className="mt-10 grid grid-cols-2 gap-8 text-[11px] text-black/70">
                            <div>
                                <div className="text-[9px] uppercase tracking-wider font-bold text-black/60 mb-1">
                                    Note
                                </div>
                                <div className="border-b border-black/40 h-12" />
                                <div className="border-b border-black/40 h-6 mt-1" />
                            </div>
                            <div>
                                <div className="text-[9px] uppercase tracking-wider font-bold text-black/60 mb-1">
                                    Compilato da
                                </div>
                                <div className="border-b border-black/40 h-12" />
                                <div className="mt-1 italic">Data ____________________</div>
                            </div>
                        </div>
                        <div className="mt-10 pt-3 border-t border-black/30 text-[9px] uppercase tracking-[0.2em] text-black/50 text-center">
                            Documento generato da RENOVA360 · Food Cost Manager
                        </div>
                    </div>
                </section>
            )}

            {(result.groups.length > 1 || (result.unassigned && result.unassigned.length > 0 && result.groups.length >= 1)) && (
                <div className="p-10">
                    <div className="border-b-2 border-black pb-4 mb-6">
                        <div
                            className="text-2xl font-bold"
                            style={{ fontFamily: "Playfair Display, serif" }}
                        >
                            Riepilogo ordine globale
                        </div>
                        <div className="text-sm text-black/70 mt-1">{restName} · {today}</div>
                    </div>
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-y-2 border-black bg-black/5">
                                <th className="text-left py-2 px-2 text-[10px] uppercase">Fornitore</th>
                                <th className="text-right py-2 px-2 text-[10px] uppercase">Articoli</th>
                                <th className="text-right py-2 px-2 text-[10px] uppercase">Subtotale</th>
                            </tr>
                        </thead>
                        <tbody>
                            {result.groups.map((g, idx) => (
                                <tr key={g.supplier_id || idx} className="border-b border-black/15">
                                    <td className="py-2 px-2 font-semibold">{g.supplier_name}</td>
                                    <td className="text-right py-2 px-2">{g.lines.length}</td>
                                    <td className="text-right py-2 px-2 font-semibold">{euro(g.total)}</td>
                                </tr>
                            ))}
                            {result.unassigned && result.unassigned.length > 0 && (
                                <tr className="border-b border-black/15 bg-amber-50">
                                    <td className="py-2 px-2 font-semibold italic">Da associare a fornitore</td>
                                    <td className="text-right py-2 px-2">{result.unassigned.length}</td>
                                    <td className="text-right py-2 px-2 text-black/40">—</td>
                                </tr>
                            )}
                        </tbody>
                        <tfoot>
                            <tr className="border-t-2 border-black">
                                <td colSpan="2" className="text-right pt-3 px-2 font-bold text-lg">
                                    TOTALE GENERALE
                                </td>
                                <td className="text-right pt-3 px-2 font-bold text-xl">{euro(result.grand_total)}</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            )}
        </div>
    );
}
